import { jwtDecode } from "jwt-decode";

// A configuracao de qual backend cada chamada deve atingir vive em
// ``utils/backendConfig.js``. Importe ``baseUrl`` (e demais simbolos
// de roteamento) DIRETO de la — nao via ``utils/utils``.

export const containsText = (text, searchText) =>
  String(text).toLowerCase().trim().indexOf(searchText.toLowerCase().trim()) >
  -1;

export const getOom = (median) => {
  let oom = {};
  if (Math.abs(median) >= 1e9) {
    oom = {
      div: 1e9,
      x: "b",
    };
  } else if (Math.abs(median) >= 1e6) {
    oom = {
      div: 1e6,
      x: "m",
    };
  } else if (Math.abs(median) >= 1e3) {
    oom = {
      div: 1e3,
      x: "k",
    };
  } else {
    oom = {
      div: 1,
      x: "",
    };
  }
  return oom;
};

export const objectsEqual = (obj1, obj2) => {
  if (!obj1 || !obj2) return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (let key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }
  return true;
};

export const deepEqual = (a, b) => {
  if (a === b) return true;
  if (
    typeof a !== "object" ||
    a === null ||
    typeof b !== "object" ||
    b === null
  )
    return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  return keysA.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(b, key) && deepEqual(a[key], b[key]),
  );
};

export const formatNumber = (input) => {
  // return number without decimals and with commas as thousand separators
  let numStr = input.toString();
  if (numStr.indexOf(".") > numStr.indexOf(",")) {
    numStr = numStr.split(".")[0];
    numStr = numStr.replace(/\./g, "");
  } else {
    numStr = numStr.split(",")[0];
    numStr = numStr.replace(/,/g, "");
  }
  return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const getFirstTwoDecimals = (num) => {
  let rounded = Number(num).toFixed(2);
  let parts = rounded.split(".");
  if (parts.length < 2) {
    return "00";
  }
  return parts[1];
};

export const onlyLettersAndSpaces = (text) => {
  return text.replace(/[^0-9a-zA-ZáéíóúüñâêîôûãõçÁÉÍÓÚÜÑÂÊÎÔÛÃÕÇ\s]/g, "");
};

export const calculateOomFromPlValues = (plValues, t) => {
  const allValues = [];

  Object.values(plValues).forEach((value) => {
    if (value !== undefined) {
      Object.values(value).forEach((val) => {
        if (typeof val === "number" && !isNaN(val) && val !== 0) {
          allValues.push(Math.abs(val));
        }
      });
    }
  });

  if (allValues.length === 0) {
    return {
      div: 1e6,
      x: " " + (t ? t("oom.m") : "m"),
    };
  }

  const sortedValues = allValues.sort((a, b) => a - b);
  const middle = Math.floor(sortedValues.length / 2);
  const median =
    sortedValues.length % 2 === 0
      ? (sortedValues[middle - 1] + sortedValues[middle]) / 2
      : sortedValues[middle];

  const autoOom = getOom(median);
  const x =
    autoOom.x === ""
      ? autoOom.x
      : " " + (t ? t("oom." + autoOom.x) : autoOom.x);

  return {
    div: autoOom.div,
    x: x,
  };
};

// Token validation utilities
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (error) {
    console.warn("Error decoding token:", error);
    return true;
  }
};

export const getTokenExpirationTime = (token) => {
  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.warn("Error decoding token:", error);
    return null;
  }
};

export const getTimeUntilTokenExpiry = (token) => {
  const expirationTime = getTokenExpirationTime(token);
  if (!expirationTime) return 0;

  const currentTime = Date.now();
  return Math.max(0, expirationTime - currentTime);
};

// Utility function for API calls that should redirect on auth failure
export const handleApiCallWithAuthRedirect = async (
  apiCall,
  onSuccess,
  onError,
  logoutUser,
) => {
  try {
    const response = await apiCall();
    if (onSuccess) {
      onSuccess(response);
    }
    return response;
  } catch (error) {
    console.log("API call failed:", error);

    // If the error is due to authentication failure (401) or token expiration logout
    if (error.response?.status === 401 || error.isTokenExpirationLogout) {
      // Force logout and redirect to login page
      if (logoutUser) {
        logoutUser(null, "Session expired. Please login again.");
      }
    } else if (onError) {
      onError(error);
    }

    throw error;
  }
};

// Specialized function for project change API calls
// This function handles the specific case where the refresh token will be blacklisted
export const handleProjectChangeApiCall = async (
  apiCall,
  onSuccess,
  onError,
  logoutUser,
  setChangingProject,
) => {
  try {
    const response = await apiCall();
    if (onSuccess) {
      onSuccess(response);
    }
    return response;
  } catch (error) {
    console.log("Project change API call failed:", error);

    // Handle specific error cases for project change
    if (error.response?.status === 403) {
      // Permission denied or validation error
      console.error("Project change validation error:", error.response.data);
      if (setChangingProject) {
        setChangingProject(false);
      }
      if (onError) {
        onError(error);
      }
    } else if (
      error.code === "ECONNABORTED" ||
      error.message?.includes("timeout")
    ) {
      // Timeout error
      console.error("Project change timeout:", error);
      if (setChangingProject) {
        setChangingProject(false);
      }
      if (onError) {
        onError(error);
      }
    } else if (error.response?.status === 401) {
      // Token expired - the axios client should have handled refresh
      // If we still get 401, it means the refresh failed or token is invalid
      if (logoutUser) {
        logoutUser(
          null,
          "Session expired. Please login again to change project.",
        );
      }
    } else {
      // Other errors
      console.error("Unexpected error during project change:", error);
      if (setChangingProject) {
        setChangingProject(false);
      }
      if (onError) {
        onError(error);
      }
    }

    throw error;
  }
};
